<script setup>
/* eslint-disable */
// Aceleres: painel "Nova conversa" deslizante (da esquerda) estilo WhatsApp Web.
// Componente de UI apenas — faz busca/lista de contatos e emite intenções de
// negócio (start-phone-conversation / open-contact-chat / edit-contact) que o
// ChatList executa (ownership, inbox, navegação). Mesmo padrão de slide do
// AceleresDropdownFilter.
import { ref, computed, watch, nextTick } from 'vue';
import { debounce } from '@chatwoot/utils';
import { AsYouType, parsePhoneNumberFromString } from 'libphonenumber-js';
import SearchAPI from 'dashboard/api/search';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import countries from 'shared/constants/countries';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';

const props = defineProps({
  show: { type: Boolean, default: false },
  accountId: { type: [String, Number], required: true },
  teleportTarget: { type: String, default: null },
  closeOnNewContactBack: { type: Boolean, default: false },
  initialView: {
    type: String,
    default: 'list',
    validator: value => ['list', 'dialer', 'new-contact'].includes(value),
  },
  slideDirection: {
    type: String,
    default: 'left',
    validator: value => ['left', 'right'].includes(value),
  },
  panelVariant: {
    type: String,
    default: 'default',
    validator: value =>
      ['default', 'contact-details', 'contacts-modal'].includes(value),
  },
  // Discador numérico no cabeçalho. É o único caminho para iniciar conversa com
  // um número que ainda não tem contato; ocultá-lo deixa só o "Novo contato".
  showDialer: { type: Boolean, default: true },
  overlayAboveHeader: { type: Boolean, default: false },
});

const emit = defineEmits([
  'close',
  'start-phone-conversation',
  'open-contact-chat',
  'open-existing-phone',
  'edit-contact',
]);

const store = useStore();

const query = ref('');
const contacts = ref([]);
const loading = ref(false);
const searchFailed = ref(false);
const searchResultQuery = ref('');
let searchRequestSequence = 0;
const activeView = ref('list');
const phoneInput = ref('');
const dialerContacts = ref([]);
const searchFieldRef = ref(null);
const phoneFieldRef = ref(null);
const newContactFirstName = ref('');
const newContactLastName = ref('');
const newContactPhoneNumber = ref('');
const newContactDialCode = ref('+55');
const newContactSaving = ref(false);
const newContactPhoneTouched = ref(false);

const outerSlideClasses = computed(() => {
  const isRight = props.slideDirection === 'right';

  return {
    enterFrom: isRight
      ? 'acl-newconv-slide-from-right'
      : 'acl-newconv-slide-from',
    leaveTo: isRight
      ? 'acl-newconv-slide-leave-to-right'
      : 'acl-newconv-slide-leave-to',
  };
});

const isContactDetailsVariant = computed(
  () => props.panelVariant === 'contact-details'
);
const isContactsModalVariant = computed(
  () => props.panelVariant === 'contacts-modal'
);

const KEYPAD_KEYS = [
  { value: '1', letters: '' },
  { value: '2', letters: 'ABC' },
  { value: '3', letters: 'DEF' },
  { value: '4', letters: 'GHI' },
  { value: '5', letters: 'JKL' },
  { value: '6', letters: 'MNO' },
  { value: '7', letters: 'PQRS' },
  { value: '8', letters: 'TUV' },
  { value: '9', letters: 'WXYZ' },
  { value: '+', letters: '' },
  { value: '0', letters: '' },
];
const countryDialOptions = countries
  .filter(country => country.dial_code)
  .map(country => ({
    value: country.dial_code,
    label: `${country.emoji || ''} ${country.dial_code}`.trim(),
    title: `${country.name} (${country.dial_code})`,
  }));

// ---- lista de contatos ----
const loadRecent = async () => {
  const requestSequence = ++searchRequestSequence;
  loading.value = true;
  searchFailed.value = false;
  searchResultQuery.value = '';
  try {
    const r = await window.axios.get(
      `/api/v1/accounts/${props.accountId}/contacts`,
      { params: { page: 1 } }
    );
    if (requestSequence !== searchRequestSequence) return;
    const list = r.data?.payload || [];
    contacts.value = list.filter(c => c.phone_number);
  } catch (e) {
    if (requestSequence !== searchRequestSequence) return;
    contacts.value = [];
    searchFailed.value = true;
  } finally {
    if (requestSequence === searchRequestSequence) loading.value = false;
  }
};

const runSearch = async q => {
  const trimmed = (q || '').trim();
  if (trimmed.length < 2) {
    await loadRecent();
    return;
  }
  const requestSequence = ++searchRequestSequence;
  const phone = parseBrazilianPhone(trimmed);
  loading.value = true;
  searchFailed.value = false;
  searchResultQuery.value = '';
  try {
    const r = await window.axios.get(
      `/api/v1/accounts/${props.accountId}/contacts/search`,
      { params: { q: phone?.nationalDigits || trimmed } }
    );
    if (requestSequence !== searchRequestSequence) return;
    const list = r?.data?.payload || [];
    // A busca do Banco de Clientes também pode localizar registros apenas
    // por identificador/documento, sem telefone. Eles devem aparecer para
    // consulta no cadastro, mesmo que não possam iniciar WhatsApp.
    contacts.value = list;
    searchResultQuery.value = trimmed;
  } catch (e) {
    if (requestSequence !== searchRequestSequence) return;
    contacts.value = [];
    searchFailed.value = true;
  } finally {
    if (requestSequence === searchRequestSequence) loading.value = false;
  }
};

const debouncedSearch = debounce(val => runSearch(val), 300);

watch(query, val => {
  searchRequestSequence += 1;
  searchResultQuery.value = '';
  debouncedSearch(val);
});

const normalizePhoneDigits = value => String(value || '').replace(/\D/g, '');
const parseBrazilianPhone = value => {
  const raw = String(value || '').trim();
  if (!raw || !/^\+?[\d\s()-]+$/.test(raw)) return null;

  const digits = raw.replace(/[\s()-]/g, '').replace(/^\+/, '');
  if (!/^\d+$/.test(digits)) return null;

  const nationalDigits =
    digits.startsWith('55') && [12, 13].includes(digits.length)
      ? digits.slice(2)
      : digits;
  if (!/^\d{10,11}$/.test(nationalDigits)) return null;

  const parsed = parsePhoneNumberFromString(`+55${nationalDigits}`);
  if (!parsed || parsed.country !== 'BR' || !parsed.isValid()) return null;

  const subscriberDigits = nationalDigits.slice(2);
  const subscriberBreak = nationalDigits.length === 11 ? 5 : 4;

  return {
    nationalDigits,
    e164: parsed.number,
    formatted: `+55 ${nationalDigits.slice(0, 2)} ${subscriberDigits.slice(
      0,
      subscriberBreak
    )}-${subscriberDigits.slice(subscriberBreak)}`,
  };
};
const searchedPhone = computed(() => parseBrazilianPhone(query.value));
const canStartSearchedPhoneConversation = computed(
  () =>
    !!searchedPhone.value &&
    !loading.value &&
    !searchFailed.value &&
    searchResultQuery.value === query.value.trim() &&
    !contacts.value.length
);
const contactPhoneDigits = contact => normalizePhoneDigits(contact?.phone_number || contact?.phoneNumber);
const contactMatchesPhone = (contact, digits) => {
  const contactDigits = contactPhoneDigits(contact);
  if (!contactDigits || digits.length < 8) return false;

  const typedTail = digits.slice(-8);
  const contactTail = contactDigits.slice(-8);
  return contactDigits.endsWith(digits) || digits.endsWith(contactDigits) || typedTail === contactTail;
};

const findDialerContact = digits => contacts.value.find(c => contactMatchesPhone(c, digits)) || null;

const normalizeContactList = list =>
  (list || [])
    .filter(c => c.phone_number || c.phoneNumber)
    .filter((contact, index, array) => {
      const id = contact.id ?? `${contact.phone_number || contact.phoneNumber}-${contact.name || ''}`;
      return array.findIndex(item => (item.id ?? `${item.phone_number || item.phoneNumber}-${item.name || ''}`) === id) === index;
    })
    .sort((a, b) => {
      const aExact = contactMatchesPhone(a, phoneDigits.value) ? 1 : 0;
      const bExact = contactMatchesPhone(b, phoneDigits.value) ? 1 : 0;
      return bExact - aExact;
    });

const searchDialerContacts = async digits => {
  if (digits.length < 2) {
    dialerContacts.value = [];
    return;
  }

  const localMatches = contacts.value.filter(c => contactMatchesPhone(c, digits));
  if (localMatches.length) {
    dialerContacts.value = normalizeContactList(localMatches);
    return;
  }

  try {
    const searchResult = await SearchAPI.get({ q: digits });
    if (phoneDigits.value !== digits) return;

    const payload = searchResult?.data?.payload || {};
    const digitList = payload.contacts || [];
    const combined = normalizeContactList([
      ...digitList,
      ...contacts.value.filter(c => contactMatchesPhone(c, digits)),
    ]);

    dialerContacts.value = combined;
  } catch (e) {
    if (phoneDigits.value === digits) dialerContacts.value = [];
  }
};

const debouncedDialerContactSearch = debounce(val => searchDialerContacts(val), 300);

// reset + carga inicial ao abrir
watch(
  () => props.show,
  async v => {
    if (!v) return;
    activeView.value = props.initialView;
    query.value = '';
    phoneInput.value = '';
    resetNewContactForm();
    dialerContacts.value = [];
    contacts.value = [];
    if (props.initialView !== 'new-contact') {
      await loadRecent();
      nextTick(() => searchFieldRef.value?.focus());
    }
  },
  { immediate: true }
);

// ---- telefone (bloco Novo contato) ----
const sanitizePhoneInput = value => {
  const raw = String(value || '');
  const hasLeadingPlus = raw.trim().startsWith('+');
  const digits = raw.replace(/\D/g, '').slice(0, 15);
  return `${hasLeadingPlus ? '+' : ''}${digits}`;
};

const formatPhoneInput = value => {
  const normalized = sanitizePhoneInput(value);
  if (!normalized) return '';

  if (normalized.startsWith('+')) {
    return new AsYouType().input(normalized) || normalized;
  }

  return new AsYouType('BR').input(normalized) || normalized;
};

const onPhoneInput = e => {
  phoneInput.value = sanitizePhoneInput(e.target.value);
};

const setPhoneInput = value => {
  phoneInput.value = sanitizePhoneInput(value);
};

const phoneDigits = computed(() => phoneInput.value.replace(/\D/g, ''));
const formattedPhoneInput = computed(() => formatPhoneInput(phoneInput.value));
const parsedPhoneInput = computed(() => parseBrazilianPhone(phoneInput.value));
const canConversar = computed(() => !!parsedPhoneInput.value);

watch(phoneDigits, digits => debouncedDialerContactSearch(digits));
const onConversar = () => {
  if (!parsedPhoneInput.value) return;
  emit('start-phone-conversation', parsedPhoneInput.value.e164);
};

const openDialer = () => {
  activeView.value = 'dialer';
  nextTick(() => phoneFieldRef.value?.focus());
};

const backFromDialer = () => {
  activeView.value = 'list';
  nextTick(() => searchFieldRef.value?.focus());
};

const onPhoneKeydown = e => {
  if (e.key === 'Enter') {
    e.preventDefault();
    onConversar();
  }
};

const appendKeypadValue = value => {
  if (value === '+') {
    if (phoneInput.value.startsWith('+')) return;
    setPhoneInput(`+${phoneDigits.value}`);
    nextTick(() => phoneFieldRef.value?.focus());
    return;
  }
  const prefix = phoneInput.value.startsWith('+') ? '+' : '';
  const nextDigits = `${phoneDigits.value}${value}`.replace(/\D/g, '').slice(0, 15);
  setPhoneInput(`${prefix}${nextDigits}`);
  nextTick(() => phoneFieldRef.value?.focus());
};

const deleteLastDigit = () => {
  if (phoneInput.value.startsWith('+') && phoneDigits.value.length === 0) {
    phoneInput.value = '';
  } else {
    const prefix = phoneInput.value.startsWith('+') ? '+' : '';
    const nextDigits = phoneDigits.value.slice(0, -1);
    phoneInput.value = nextDigits.length || prefix === '+' ? `${prefix}${nextDigits}` : '';
  }
  nextTick(() => phoneFieldRef.value?.focus());
};

const onMessageContact = c => emit('open-contact-chat', c);
const onEditContact = c => emit('edit-contact', c);
const close = () => emit('close');
function resetNewContactForm() {
  newContactFirstName.value = '';
  newContactLastName.value = '';
  newContactPhoneNumber.value = '';
  newContactDialCode.value = '+55';
  newContactSaving.value = false;
  newContactPhoneTouched.value = false;
}

const displayName = c => c.name || c.phone_number || 'Contato';
const displayPhone = c => {
  const p = c.phone_number || c.phoneNumber || '';
  return p.startsWith('+') ? p : p ? `+${p}` : '';
};
const displayIdentifier = c => {
  const instagram =
    c.additional_attributes?.social_profiles?.instagram ||
    c.custom_attributes?.social_profiles?.instagram;
  if (instagram) {
    const username = String(instagram)
      .replace(/^https?:\/\/(?:www\.)?instagram\.com\//i, '')
      .replace(/^@/, '')
      .split('/')[0];
    return `Instagram: @${username}`;
  }
  return c.identifier || c.email || '';
};

const startSearchedPhoneConversation = () => {
  if (!canStartSearchedPhoneConversation.value) return;
  emit('start-phone-conversation', searchedPhone.value.e164);
};

const hasDialerMatches = computed(() => dialerContacts.value.length > 0);
const newContactPhoneDigits = computed(() =>
  String(newContactPhoneNumber.value || '').replace(/\D/g, '')
);
const newContactFullPhone = computed(
  () => `${newContactDialCode.value}${newContactPhoneDigits.value}`
);
const newContactPhoneError = computed(() => {
  const digits = newContactPhoneDigits.value;
  if (!digits) return 'Informe o telefone.';
  if (newContactDialCode.value === '+55' && ![10, 11].includes(digits.length)) {
    return 'Informe 10 ou 11 dígitos, incluindo o DDD.';
  }
  const parsed = parsePhoneNumberFromString(newContactFullPhone.value);
  if (!parsed?.isValid()) {
    return 'Informe um número válido para o DDI selecionado.';
  }
  return '';
});
const isNewContactPhoneValid = computed(() => !newContactPhoneError.value);
const canSubmitNewContact = computed(
  () =>
    !!newContactFirstName.value.trim() &&
    isNewContactPhoneValid.value
);
const openNewContact = () => {
  resetNewContactForm();
  const raw = query.value.trim();
  if (/^\+?[\d\s()-]+$/.test(raw)) {
    const digits = raw.replace(/\D/g, '');
    if (raw.startsWith('+')) {
      const formatted = new AsYouType();
      formatted.input(raw);
      const callingCode = formatted.getCallingCode();
      if (
        callingCode &&
        countryDialOptions.some(option => option.value === `+${callingCode}`)
      ) {
        newContactDialCode.value = `+${callingCode}`;
        newContactPhoneNumber.value = digits.slice(callingCode.length);
      } else {
        newContactPhoneNumber.value = digits;
      }
    } else {
      newContactPhoneNumber.value =
        digits.startsWith('55') && [12, 13].includes(digits.length)
          ? digits.slice(2)
          : digits;
    }
    newContactPhoneTouched.value = !!newContactPhoneNumber.value;
  }
  activeView.value = 'new-contact';
};
const backFromNewContact = () => {
  if (props.closeOnNewContactBack) {
    close();
    return;
  }

  activeView.value = 'list';
  nextTick(() => searchFieldRef.value?.focus());
};
const onNewContactPhoneInput = event => {
  const digits = String(event.target.value || '')
    .replace(/\D/g, '')
    .slice(0, 15);
  newContactPhoneNumber.value = digits;
  event.target.value = digits;
  newContactPhoneTouched.value = true;
};
const createNewContact = async () => {
  if (!canSubmitNewContact.value || newContactSaving.value) return;

  const name = [newContactFirstName.value.trim(), newContactLastName.value.trim()]
    .filter(Boolean)
    .join(' ');

  const fullPhone = newContactFullPhone.value;

  newContactSaving.value = true;
  try {
    const contact = await store.dispatch('contacts/create', {
      name,
      phoneNumber: fullPhone,
    });
    useAlert('Contato criado com sucesso.');
    resetNewContactForm();
    emit('open-contact-chat', contact);
  } catch (error) {
    // Telefone ja cadastrado na conta. Antes, este caminho tentava localizar o
    // contato existente pela BUSCA -- que aplica a visibilidade -- e, quando o
    // numero era de outro vendedor, nao achava nada e a tela virava um beco sem
    // saida: "o numero ja esta cadastrado" sem o contato aparecer em lugar
    // nenhum. Quem sabe responder isso e o fluxo de iniciar conversa por
    // telefone: ele consulta o dono na conta INTEIRA e mostra o aviso de lead
    // de outro vendedor com o pedido de autorizacao, ou abre a conversa que ja
    // existe. Entao o telefone e entregue a ele.
    if (error?.duplicatePhone) {
      resetNewContactForm();
      emit('open-existing-phone', fullPhone);
      return;
    }

    useAlert(
      error?.data ||
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Nao foi possivel criar o contato agora.'
    );
    newContactSaving.value = false;
  }
};
</script>

<template>
  <!-- eslint-disable -->
  <div>
    <!-- backdrop transparente p/ click-fora fechar (sem dim visual) -->
    <Teleport to="body">
      <div
        v-if="show"
        class="fixed inset-0"
        :class="overlayAboveHeader ? 'z-[10001]' : 'z-40'"
        @click="close"
      />
    </Teleport>

    <Teleport
      :to="teleportTarget || 'body'"
      :disabled="!teleportTarget"
    >
      <Transition
        enter-active-class="acl-newconv-slide-enter-active"
        :enter-from-class="outerSlideClasses.enterFrom"
        enter-to-class="acl-newconv-slide-to"
        leave-active-class="acl-newconv-slide-leave-active"
        leave-from-class="acl-newconv-slide-to"
        :leave-to-class="outerSlideClasses.leaveTo"
      >
        <div
          v-if="show"
          class="acl-new-conv-drawer__content absolute inset-0 bg-white dark:bg-n-solid-1 flex flex-col"
          :class="[
            overlayAboveHeader ? 'z-[10002]' : 'z-50',
            {
              'acl-new-conv-drawer__content--contact-details':
                isContactDetailsVariant,
            },
          ]"
          tabindex="-1"
          @keydown.escape="close"
        >
        <!-- Cabeçalho principal -->
        <div class="px-4 pt-4 pb-2 flex-shrink-0">
          <div class="flex items-center justify-between gap-3">
            <button
              type="button"
              class="acl-new-conv-header__icon-btn size-10 max-md:!size-11"
              aria-label="Voltar"
              @click="close"
            >
              <span class="i-lucide-arrow-left size-5" />
            </button>
            <h3 class="flex-1 min-w-0 text-xl font-semibold text-n-slate-12 text-left">
              {{ activeView === 'new-contact' ? 'Novo contato' : 'Nova conversa' }}
            </h3>
            <button
              v-if="showDialer"
              type="button"
              class="acl-new-conv-header__icon-btn size-10 max-md:!size-11"
              aria-label="Abrir discador"
              @click="openDialer"
            >
              <span class="i-lucide-grip size-5 max-md:size-[22px]" />
            </button>
          </div>
        </div>

        <!-- Campo de pesquisa -->
        <div class="px-0 pb-3 flex-shrink-0">
          <div
            class="acl-quick-search-wrap relative"
            :class="{
              'acl-quick-search-wrap--contacts-modal max-md:!px-6':
                isContactsModalVariant,
            }"
          >
            <div class="acl-search-pill">
              <svg
                class="acl-search-pill__icon"
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="7" />
                <path d="m21 21-4.3-4.3" />
              </svg>
              <input
                ref="searchFieldRef"
                v-model="query"
                type="text"
                placeholder="Pesquisar nome ou número"
                class="acl-search-pill__input max-md:!h-11 max-md:!leading-[44px]"
              />
              <button
                v-if="query"
                type="button"
                class="acl-search-pill__clear max-md:!size-11"
                aria-label="Limpar"
                @click="query = ''"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </div>

          <button
            type="button"
            class="acl-new-contact-trigger"
            @click="openNewContact"
          >
            <span class="acl-new-contact-trigger__avatar">
              <span class="i-lucide-user-plus size-5" />
            </span>
            <span class="acl-new-contact-trigger__label">Novo contato</span>
          </button>
        </div>

        <!-- Body scrollável -->
        <div class="flex-1 min-h-0 overflow-y-auto px-4 pb-4 scrollbar-thin">
          <!-- Lista Contatos -->
          <div class="pt-2">
            <p class="text-sm font-semibold text-n-slate-12 px-1 pb-2">
              Contatos
            </p>

            <div v-if="loading" class="py-6 text-center text-xs text-n-slate-9">
              Carregando…
            </div>

            <div
              v-else-if="!contacts.length"
              class="py-6 text-xs text-n-slate-9"
            >
              <template v-if="canStartSearchedPhoneConversation">
                <p class="px-1 pb-2 text-sm font-semibold text-n-slate-12">
                  Não estão na sua lista de contatos
                </p>
                <div class="flex items-center gap-3 py-2.5">
                  <Avatar
                    :name="searchedPhone.formatted"
                    :size="42"
                    rounded-full
                  />
                  <span class="min-w-0 flex-1 text-sm text-n-slate-12">
                    {{ searchedPhone.formatted }}
                  </span>
                  <button
                    type="button"
                    class="min-h-11 flex-shrink-0 px-2 text-sm font-semibold text-[#1b8755]"
                    :aria-label="`Conversar com ${searchedPhone.formatted}`"
                    @click="startSearchedPhoneConversation"
                  >
                    Conversar
                  </button>
                </div>
              </template>
              <p v-else class="py-6 text-center">
                Nenhum contato encontrado.
              </p>
            </div>

            <ul v-else class="divide-y divide-n-weak/60">
              <li
                v-for="c in contacts"
                :key="c.id"
                class="flex items-center gap-3 py-2.5 cursor-pointer"
                @click="onMessageContact(c)"
              >
                <Avatar
                  :name="displayName(c)"
                  :src="c.thumbnail"
                  :size="42"
                  rounded-full
                />
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-semibold text-n-slate-12 truncate">
                    {{ displayName(c) }}
                  </p>
                  <p class="text-xs text-n-slate-9 break-all">
                    {{ displayPhone(c) || displayIdentifier(c) }}
                  </p>
                </div>
                <button
                  type="button"
                  class="inline-flex min-h-11 flex-shrink-0 items-center justify-center gap-1.5 rounded-full bg-[#00a884] px-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#008f6f] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#00a884] md:hidden"
                  :aria-label="`Iniciar conversa com ${displayName(c)}`"
                  @click.stop="onMessageContact(c)"
                >
                  <span class="i-lucide-play size-3.5" aria-hidden="true" />
                  <span>Iniciar</span>
                </button>
              </li>
            </ul>
          </div>
        </div>

        <Transition
          enter-active-class="acl-newconv-slide-enter-active"
          enter-from-class="acl-newconv-slide-from-right"
          enter-to-class="acl-newconv-slide-to"
          leave-active-class="acl-newconv-slide-leave-active"
          leave-from-class="acl-newconv-slide-to"
          leave-to-class="acl-newconv-slide-leave-to-right"
        >
          <div
            v-if="activeView === 'dialer'"
            class="acl-new-conv-dialer absolute inset-0 z-10 bg-white dark:bg-n-solid-1 flex flex-col"
          >
            <div class="acl-new-conv-dialer__header flex-shrink-0">
              <div class="flex items-center gap-3">
                <button
                  type="button"
                  class="acl-new-conv-dialer__back max-md:!size-11"
                  aria-label="Voltar para nova conversa"
                  @click="backFromDialer"
                >
                  <span class="i-lucide-arrow-left size-5" />
                </button>
                <h3 class="acl-new-conv-dialer__title">
                  Telefone
                </h3>
              </div>
            </div>

            <div class="acl-new-conv-dialer__body flex-1 min-h-0 flex flex-col">
              <div class="acl-new-conv-dialer__input-wrap">
                <input
                  ref="phoneFieldRef"
                  :value="formattedPhoneInput"
                  @input="onPhoneInput"
                  @keydown="onPhoneKeydown"
                  type="tel"
                  inputmode="tel"
                  placeholder=""
                  class="acl-new-conv-dialer__input max-md:!h-11"
                  autofocus
                />
              </div>

              <div class="acl-new-conv-dialer__spacer">
                <div
                  v-if="hasDialerMatches"
                  class="acl-new-conv-dialer__matches"
                >
                  <button
                    v-for="contact in dialerContacts"
                    :key="contact.id || `${contact.phone_number || contact.phoneNumber}-${contact.name || ''}`"
                    type="button"
                    class="acl-new-conv-dialer__contact"
                    @click="onMessageContact(contact)"
                  >
                    <Avatar
                      :name="displayName(contact)"
                      :src="contact.thumbnail"
                      :size="42"
                      rounded-full
                    />
                    <span class="acl-new-conv-dialer__contact-text">
                      <span class="acl-new-conv-dialer__contact-name">
                        {{ displayName(contact) }}
                      </span>
                      <span class="acl-new-conv-dialer__contact-phone">
                        {{ displayPhone(contact) }}
                      </span>
                    </span>
                  </button>
                </div>
                <p
                  v-else-if="!phoneInput"
                  class="acl-new-conv-dialer__hint max-md:!whitespace-normal"
                >
                  Insira um número de telefone para iniciar uma conversa.
                </p>
                <p
                  v-else
                  class="acl-new-conv-dialer__hint"
                >
                  Nenhum contato encontrado.
                </p>
              </div>

              <button
                type="button"
                class="flex min-h-12 w-full items-center justify-center rounded-lg bg-[#1b8755] px-4 text-base font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50 md:hidden"
                :disabled="!canConversar"
                @click="onConversar"
              >
                Conversar
              </button>

              <div class="acl-new-conv-dialer__keypad max-md:!hidden">
                <button
                  v-for="keyItem in KEYPAD_KEYS"
                  :key="keyItem.value"
                  type="button"
                  class="acl-new-conv-dialer__key"
                  @click="appendKeypadValue(keyItem.value)"
                >
                  <span class="acl-new-conv-dialer__value">{{ keyItem.value }}</span>
                  <span
                    v-if="keyItem.letters || keyItem.value === '1'"
                    class="acl-new-conv-dialer__letters"
                  >
                    {{ keyItem.letters }}
                  </span>
                </button>
                <button
                  type="button"
                  class="acl-new-conv-dialer__key acl-new-conv-dialer__key--delete"
                  :disabled="!phoneInput.length"
                  aria-label="Apagar último dígito"
                  @click="deleteLastDigit"
                >
                  <span class="i-lucide-delete acl-new-conv-dialer__delete-icon size-6" />
                </button>
              </div>
            </div>
          </div>
        </Transition>

        <Transition
          enter-active-class="acl-newconv-slide-enter-active"
          enter-from-class="acl-newconv-slide-from-right"
          enter-to-class="acl-newconv-slide-to"
          leave-active-class="acl-newconv-slide-leave-active"
          leave-from-class="acl-newconv-slide-to"
          leave-to-class="acl-newconv-slide-leave-to-right"
        >
          <div
            v-if="activeView === 'new-contact'"
            class="acl-new-conv-dialer absolute inset-0 z-10 bg-white dark:bg-n-solid-1 flex flex-col"
          >
            <div class="acl-new-conv-dialer__header flex-shrink-0">
              <div class="flex items-center gap-3">
                <button
                  type="button"
                  class="acl-new-conv-dialer__back max-md:!size-11"
                  :aria-label="
                    isContactDetailsVariant
                      ? 'Fechar dados do contato'
                      : 'Voltar para nova conversa'
                  "
                  @click="
                    isContactDetailsVariant ? close() : backFromNewContact()
                  "
                >
                  <span
                    :class="
                      isContactDetailsVariant
                        ? 'i-lucide-x size-5'
                        : 'i-lucide-arrow-left size-5'
                    "
                  />
                </button>
                <h3 class="acl-new-conv-dialer__title">
                  Novo contato
                </h3>
              </div>
            </div>

            <div class="acl-new-contact-panel">
              <label class="acl-new-contact-panel__field">
                <span class="acl-new-contact-panel__label">Nome</span>
                <input
                  v-model="newContactFirstName"
                  type="text"
                  class="acl-new-contact-panel__input"
                />
              </label>

              <label class="acl-new-contact-panel__field">
                <span class="acl-new-contact-panel__label">Sobrenome (opcional)</span>
                <input
                  v-model="newContactLastName"
                  type="text"
                  class="acl-new-contact-panel__input"
                />
              </label>

              <div class="acl-new-contact-panel__field">
                <span class="acl-new-contact-panel__label">Telefone</span>
                <div class="acl-new-contact-panel__phone-row">
                  <select
                    v-model="newContactDialCode"
                    class="acl-new-contact-panel__select"
                    aria-label="Codigo do pais"
                    @change="newContactPhoneTouched = true"
                  >
                    <option
                      v-for="option in countryDialOptions"
                      :key="`${option.value}-${option.label}`"
                      :value="option.value"
                      :title="option.title"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                  <input
                    :value="newContactPhoneNumber"
                    type="tel"
                    inputmode="tel"
                    placeholder="DDD + numero. Ex: 51995116669"
                    class="acl-new-contact-panel__input acl-new-contact-panel__input--phone"
                    :class="{
                      'acl-new-contact-panel__input--invalid':
                        newContactPhoneTouched && newContactPhoneError,
                    }"
                    :aria-invalid="!!(newContactPhoneTouched && newContactPhoneError)"
                    :aria-describedby="
                      newContactPhoneTouched && newContactPhoneError
                        ? 'acl-new-contact-phone-error'
                        : undefined
                    "
                    @input="onNewContactPhoneInput"
                  />
                </div>
                <p
                  v-if="newContactPhoneTouched && newContactPhoneError"
                  id="acl-new-contact-phone-error"
                  class="acl-new-contact-panel__error"
                  role="alert"
                >
                  {{ newContactPhoneError }}
                </p>
              </div>

              <div class="acl-new-contact-panel__footer">
                <p class="acl-new-contact-panel__hint">
                  Use somente numeros no telefone. O DDI selecionado sera aplicado automaticamente.
                </p>
                <button
                  type="button"
                  class="acl-new-contact-panel__submit"
                  :disabled="!canSubmitNewContact || newContactSaving"
                  @click="createNewContact"
                >
                  {{ newContactSaving ? 'Criando contato...' : 'Criar contato' }}
                </button>
              </div>
            </div>
          </div>
        </Transition>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
.acl-new-conv-dialer {
  color: #111111;
}
.acl-new-conv-dialer__header {
  padding: 16px 16px 8px;
  border-bottom: 0;
}
.acl-new-conv-dialer__back {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  color: #111111;
  border: 0;
  background: transparent;
  padding: 0;
}
.acl-new-conv-dialer__title {
  margin: 0;
  flex: 1;
  min-width: 0;
  font-size: 20px;
  line-height: 1.2;
  font-weight: 600;
  color: #111111;
  text-align: left;
}
.acl-new-conv-drawer__content--contact-details .acl-new-conv-dialer__header {
  display: flex;
  min-height: 64px;
  align-items: center;
  padding: 0 20px;
  border-bottom: 1px solid rgb(var(--n-weak));
}
.acl-new-conv-drawer__content--contact-details .acl-new-conv-dialer__header > div {
  width: 100%;
}
.acl-new-conv-drawer__content--contact-details .acl-new-conv-dialer__back {
  width: 32px;
  height: 32px;
  border-radius: 9999px;
}
.acl-new-conv-drawer__content--contact-details .acl-new-conv-dialer__back:hover {
  background: rgb(var(--n-alpha-2));
}
.acl-new-conv-drawer__content--contact-details .acl-new-conv-dialer__title {
  font-size: 16px;
}
.acl-new-conv-dialer__body {
  padding: 0 16px 16px;
  gap: 50px !important;
}
.acl-new-conv-dialer__input-wrap {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 0 6px !important;
  background: transparent;
  border: 0;
  border-bottom: 1px solid #1b8755;
  border-radius: 0;
  box-shadow: none;
  transition: none;
}
.acl-new-conv-dialer__input {
  flex: 1;
  min-width: 0;
  height: 40px;
  width: 100%;
  border: none !important;
  background: transparent !important;
  color: #111111;
  font-size: 17px;
  line-height: 40px;
  font-weight: 600;
  padding: 0;
  margin: 0;
  outline: none !important;
  box-shadow: none !important;
}
.acl-new-conv-dialer__input::placeholder {
  color: #8a8a8a;
}
.acl-new-conv-dialer__input:focus,
.acl-new-conv-dialer__input:focus-visible,
.acl-new-conv-dialer__input:focus-within {
  border: none !important;
  background: transparent !important;
  outline: none !important;
  box-shadow: none !important;
}
.acl-quick-search-wrap {
  padding: 6px 16px !important;
  margin: 0 0 0.75rem;
}
.acl-search-pill {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 3px 14px !important;
  background: rgba(194, 189, 184, .15);
  border: none;
  border-radius: 50px;
  box-shadow: inset 0 0 0 0 transparent;
  transition: box-shadow .12s;
}
.acl-search-pill:hover {
  box-shadow: inset 0 0 0 1px #c4c4c4;
}
.acl-search-pill:focus-within {
  box-shadow: inset 0 0 0 1.5px #1b8755;
}
.acl-search-pill__icon {
  flex-shrink: 0;
  color: #8a8a8a;
}
.acl-search-pill__input {
  flex: 1;
  min-width: 0;
  height: 40px;
  background: transparent !important;
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
  padding: 0;
  margin: 0;
  font-size: 13px;
  color: #1f2937;
  line-height: 40px;
}
.acl-search-pill__input::placeholder {
  color: #8a8a8a;
}
.acl-search-pill__input:focus,
.acl-search-pill__input:focus-visible,
.acl-search-pill__input:focus-within {
  outline: none !important;
  box-shadow: none !important;
  border: none !important;
  background: transparent !important;
}
.acl-search-pill__clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  background: transparent;
  border: none;
  border-radius: 9999px;
  color: #8a8a8a;
  cursor: pointer;
  padding: 0;
  transition: background-color .12s, color .12s;
}
.acl-search-pill__clear:hover {
  background: rgba(0, 0, 0, .06);
  color: #4b5563;
}
.acl-search-pill__clear:focus-visible {
  outline: none;
}
:global(.dark) .acl-search-pill {
  background: rgba(255, 255, 255, .04);
}
:global(.dark) .acl-search-pill:hover {
  box-shadow: inset 0 0 0 1px #3f3f46;
}
:global(.dark) .acl-search-pill__icon,
:global(.dark) .acl-search-pill__input::placeholder,
:global(.dark) .acl-search-pill__clear {
  color: #a1a1aa;
}
:global(.dark) .acl-search-pill__input {
  color: #e5e5e5;
}
:global(.dark) .acl-search-pill__clear:hover {
  background: rgba(255, 255, 255, .08);
  color: #e5e5e5;
}
.acl-new-contact-trigger {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 12px;
  padding: 4px 16px 0;
  border: 0;
  background: transparent;
  text-align: left;
  color: #111111;
}
.acl-new-contact-trigger__avatar {
  display: inline-flex;
  width: 42px;
  height: 42px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  background: #1b8755;
  color: #ffffff;
}
.acl-new-contact-trigger__label {
  font-size: 14px;
  line-height: 1.25;
  font-weight: 600;
}
.acl-new-contact-panel {
  display: flex;
  min-height: 0;
  flex: 1;
  flex-direction: column;
  padding: 12px 16px 24px;
}
.acl-new-contact-panel__field {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 18px;
}
.acl-new-contact-panel__label {
  font-size: 12px;
  line-height: 1.2;
  font-weight: 600;
  color: #6b7280;
}
.acl-new-contact-panel__input,
.acl-new-contact-panel__select {
  width: 100%;
  height: 44px;
  border: 0;
  border-bottom: 1px solid #1b8755;
  border-radius: 0;
  background: transparent;
  padding: 0;
  font-size: 17px;
  line-height: 44px;
  font-weight: 500;
  color: #111111;
  outline: none;
  box-shadow: none;
}
.acl-new-contact-panel__phone-row {
  display: grid;
  grid-template-columns: minmax(0, 112px) minmax(0, 1fr);
  gap: 12px;
}
.acl-new-contact-panel__error {
  margin: 4px 0 0;
  color: #dc2626;
  font-size: 12px;
  line-height: 1.4;
}
.acl-new-contact-panel__select {
  appearance: auto;
  height: 44px;
  min-height: 44px;
  border: 1px solid #e1e4e8;
  border-radius: 10px;
  background: #f7f7f8;
  padding: 0 10px;
  color: #111827;
  font-size: 14px;
  line-height: normal;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
}
.acl-new-conv-drawer__content--contact-details .acl-new-conv-dialer__input-wrap {
  border-bottom-color: #d1d5db;
  transition: border-color 0.15s ease;
}
.acl-new-conv-drawer__content--contact-details .acl-new-conv-dialer__input-wrap:focus-within {
  border-bottom-color: #1b8755;
}
.acl-new-conv-drawer__content--contact-details .acl-new-contact-panel__input,
.acl-new-conv-drawer__content--contact-details .acl-new-contact-panel__select {
  border: 0;
  border-bottom: 1px solid #d1d5db;
  border-radius: 0;
  background: transparent;
  padding: 0;
  box-shadow: none;
  transition: border-color 0.15s ease;
}
.acl-new-conv-drawer__content--contact-details .acl-new-contact-panel__input:focus,
.acl-new-conv-drawer__content--contact-details .acl-new-contact-panel__input:focus-visible,
.acl-new-conv-drawer__content--contact-details .acl-new-contact-panel__select:focus,
.acl-new-conv-drawer__content--contact-details .acl-new-contact-panel__select:focus-visible {
  border-bottom-color: #1b8755;
  outline: none;
}
.acl-new-contact-panel__input--invalid,
.acl-new-contact-panel__input--invalid:focus,
.acl-new-conv-drawer__content--contact-details .acl-new-contact-panel__input--invalid,
.acl-new-conv-drawer__content--contact-details .acl-new-contact-panel__input--invalid:focus {
  border-bottom-color: #dc2626;
}
.acl-new-contact-panel__footer {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 4px;
  padding-top: 0;
}
.acl-new-contact-panel__hint {
  margin: 0;
  color: #6b7280;
  font-size: 12px;
  line-height: 1.5;
  text-align: left;
}
.acl-new-contact-panel__submit {
  display: inline-flex;
  width: 100%;
  min-height: 48px;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 12px;
  background: #1b8755;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
}
.acl-new-contact-panel__submit:disabled {
  background: #cbd5e1;
  color: #64748b;
  cursor: not-allowed;
}
:global(.dark) .acl-new-contact-trigger,
:global(.dark) .acl-new-contact-panel__input,
:global(.dark) .acl-new-contact-panel__select {
  color: #f3f4f6;
}
:global(.dark) .acl-new-contact-panel__label {
  color: #a1a1aa;
}
:global(.dark) .acl-new-contact-panel__hint {
  color: #a1a1aa;
}
:global(.dark) .acl-new-contact-panel__submit:disabled {
  background: #334155;
  color: #94a3b8;
}
.acl-new-conv-dialer__spacer {
  flex: 0 0 auto;
  min-height: 92px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 0 12px;
}
.acl-new-conv-dialer__matches {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.acl-new-conv-dialer__hint {
  margin: 0;
  max-width: 100%;
  text-align: center;
  color: #b3b3b3;
  font-size: 15px;
  line-height: 1.5;
  font-weight: 400;
  white-space: nowrap;
}
.acl-new-conv-dialer__contact {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  max-width: 100%;
  min-width: 0;
  padding: 6px 10px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #111111;
  cursor: pointer;
}
.acl-new-conv-dialer__contact:hover,
.acl-new-conv-dialer__contact:focus-visible {
  background: rgba(0, 0, 0, 0.06);
}
.acl-new-conv-dialer__contact-text {
  display: flex;
  min-width: 0;
  flex-direction: column;
  align-items: flex-start;
}
.acl-new-conv-dialer__contact-name,
.acl-new-conv-dialer__contact-phone {
  display: block;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.acl-new-conv-dialer__contact-name {
  color: #111111;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.25;
}
.acl-new-conv-dialer__contact-phone {
  color: #737373;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.3;
}
.acl-new-conv-dialer__keypad {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px 8px !important;
  padding: 0 0 2px;
}
.acl-new-conv-dialer__key {
  min-height: 64px;
  border: 0;
  border-radius: 0;
  background: transparent;
  color: #111111;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-weight: 400;
  line-height: 1;
}
.acl-new-conv-dialer__key:hover,
.acl-new-conv-dialer__key:focus-visible {
  background: rgba(0, 0, 0, 0.06);
  border-radius: 10px !important;
}
.acl-new-conv-dialer__value {
  display: block;
  font-size: 29px;
  line-height: 1;
  font-weight: 400;
}
.acl-new-conv-dialer__letters {
  display: block;
  min-height: 12px;
  color: #a3a3a3;
  font-size: 14.5px;
  font-weight: 400;
  letter-spacing: 0;
}
.acl-new-conv-dialer__key--delete {
  justify-self: center;
}
.acl-new-conv-dialer__delete-icon {
  color: #111111;
}
.acl-new-conv-dialer__key:disabled {
  opacity: 1;
  cursor: default;
}
/* Slide da esquerda p/ direita — réplica do AceleresDropdownFilter
   (cubic-bezier easeOutCubic, 380ms). Leave é transform puro p/ o deslize
   permanecer visível os 380ms inteiros. */
.acl-newconv-slide-enter-active {
  transition: transform 380ms cubic-bezier(.22, .61, .36, 1),
              opacity 220ms ease-out;
  will-change: transform, opacity;
}
.acl-newconv-slide-leave-active {
  transition: transform 380ms cubic-bezier(.22, .61, .36, 1);
  will-change: transform;
}
.acl-newconv-slide-from {
  transform: translateX(-100%);
  opacity: 0;
}
.acl-newconv-slide-from-right {
  transform: translateX(100%);
  opacity: 0;
}
.acl-newconv-slide-to {
  transform: translateX(0);
  opacity: 1;
}
.acl-newconv-slide-leave-to {
  transform: translateX(-100%);
}
.acl-newconv-slide-leave-to-right {
  transform: translateX(100%);
}

:global(.dark) .acl-new-conv-dialer {
  color: #f3f4f6;
}
:global(.dark) .acl-new-conv-dialer__back,
:global(.dark) .acl-new-conv-dialer__title,
:global(.dark) .acl-new-conv-dialer__input,
:global(.dark) .acl-new-conv-dialer__key,
:global(.dark) .acl-new-conv-dialer__delete-icon {
  color: #f3f4f6;
}
:global(.dark) .acl-new-conv-dialer__back {
  background: transparent;
}
:global(.dark) .acl-new-conv-dialer__hint,
:global(.dark) .acl-new-conv-dialer__contact-phone,
:global(.dark) .acl-new-conv-dialer__letters {
  color: #9ca3af;
}
:global(.dark) .acl-new-conv-dialer__input {
  border: none !important;
}
:global(.dark) .acl-new-conv-dialer__input-wrap {
  background: transparent;
  border-bottom-color: #1b8755;
}

:global(.dark) .acl-new-conv-dialer__contact-name {
  color: #f3f4f6;
}

:global(.dark) .acl-new-conv-dialer__contact:hover,
:global(.dark) .acl-new-conv-dialer__contact:focus-visible {
  background: rgba(255, 255, 255, 0.06);
}

:global(.dark) .acl-new-conv-dialer__key:hover,
:global(.dark) .acl-new-conv-dialer__key:focus-visible {
  background: rgba(255, 255, 255, 0.06);
}
</style>
